/**
 * Image crop dialog for the Social Post Composer — 2026-07 visual overhaul.
 * • Ratio buttons show a mini frame glyph + platform hint so 1:1 / 4:5 / 9:16 /
 *   16:9 are visually obvious.
 * • Strong gold crop frame with corner handles + rule-of-thirds grid; outside
 *   area heavily dimmed.
 * • Frame is draggable AND resizable (bottom-right handle) within the ratio.
 * Videos never enter this dialog (they upload as-is). Output URLs feed the
 * publish engine unchanged.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Crop, Check, SkipForward } from 'lucide-react';

const RATIOS = [
  { id: '1:1',  label: '1:1',  hint: 'Square · IG grid',          v: 1,       glyph: { w: 18, h: 18 } },
  { id: '4:5',  label: '4:5',  hint: 'Portrait · IG feed',        v: 4 / 5,   glyph: { w: 15, h: 19 } },
  { id: '9:16', label: '9:16', hint: 'Story / Reel',              v: 9 / 16,  glyph: { w: 11, h: 20 } },
  { id: '16:9', label: '16:9', hint: 'Landscape · FB',            v: 16 / 9,  glyph: { w: 22, h: 12 } },
];
const MIN_SCALE = 0.3;

export function ImageCropDialog({ open, files, lockRatio, onDone, onCancel }) {
  const [idx, setIdx] = useState(0);
  const [ratioId, setRatioId] = useState('1:1');
  const [imgUrl, setImgUrl] = useState(null);
  const [disp, setDisp] = useState(null); // {w,h,nw,nh}
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [scale, setScale] = useState(1); // fraction of the max-fit crop size
  const [results, setResults] = useState([]);
  const imgRef = useRef(null);
  const dragRef = useRef(null);

  const file = files?.[idx];
  const ratioDef = RATIOS.find((r) => r.id === ratioId) || RATIOS[0];
  const ratio = ratioDef.v;

  useEffect(() => {
    if (!open) { setIdx(0); setResults([]); return; }
    setOffset({ x: 0, y: 0 });
    setScale(1);
  }, [open, idx]);

  useEffect(() => {
    if (!file) { setImgUrl(null); return undefined; }
    const url = URL.createObjectURL(file);
    setImgUrl(url);
    setDisp(null);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const onImgLoad = () => {
    const el = imgRef.current;
    if (!el) return;
    setDisp({ w: el.clientWidth, h: el.clientHeight, nw: el.naturalWidth, nh: el.naturalHeight });
    setOffset({ x: 0, y: 0 });
    setScale(1);
  };

  // Max-fit crop window for this ratio, then apply the user's scale.
  let cw = 0; let ch = 0;
  if (disp) {
    const maxW = Math.min(disp.w, disp.h * ratio);
    cw = maxW * scale;
    ch = cw / ratio;
  }
  const maxX = disp ? disp.w - cw : 0;
  const maxY = disp ? disp.h - ch : 0;

  useEffect(() => {
    // Recenter + reset size when the ratio changes so the shape change is obvious.
    if (!disp) return;
    const maxW = Math.min(disp.w, disp.h * ratio);
    setScale(1);
    setOffset({ x: Math.max(0, (disp.w - maxW) / 2), y: Math.max(0, (disp.h - maxW / ratio) / 2) });
  }, [ratioId, disp]); // eslint-disable-line react-hooks/exhaustive-deps

  const clampOffset = (x, y, w, h) => ({
    x: Math.min(Math.max(0, disp.w - w), Math.max(0, x)),
    y: Math.min(Math.max(0, disp.h - h), Math.max(0, y)),
  });

  const startDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = { mode: 'move', sx: e.clientX, sy: e.clientY, ox: offset.x, oy: offset.y };
    attachDragListeners();
  };

  const startResize = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = { mode: 'resize', sx: e.clientX, sy: e.clientY, sScale: scale, ox: offset.x, oy: offset.y };
    attachDragListeners();
  };

  const attachDragListeners = () => {
    const move = (ev) => {
      const d = dragRef.current;
      if (!d || !disp) return;
      if (d.mode === 'move') {
        setOffset(clampOffset(d.ox + (ev.clientX - d.sx), d.oy + (ev.clientY - d.sy), cw, ch));
      } else {
        const maxW = Math.min(disp.w, disp.h * ratio);
        const delta = Math.max(ev.clientX - d.sx, (ev.clientY - d.sy) * ratio);
        let ns = (d.sScale * maxW + delta) / maxW;
        ns = Math.min(1, Math.max(MIN_SCALE, ns));
        // Keep top-left anchored; clamp so the frame stays inside the image.
        const nw = maxW * ns;
        const nh = nw / ratio;
        setScale(ns);
        setOffset(clampOffset(d.ox, d.oy, nw, nh));
      }
    };
    const up = () => {
      dragRef.current = null;
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  };

  const finish = useCallback((newResults) => {
    if (idx + 1 < (files?.length || 0)) {
      setResults(newResults);
      setIdx(idx + 1);
    } else {
      onDone(newResults);
    }
  }, [idx, files, onDone]);

  const applyCrop = () => {
    if (!disp || !imgRef.current) return;
    const k = disp.nw / disp.w;
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(cw * k);
    canvas.height = Math.round(ch * k);
    const ctx = canvas.getContext('2d');
    ctx.drawImage(imgRef.current, offset.x * k, offset.y * k, cw * k, ch * k, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      const base = (file.name || 'image').replace(/\.[^.]+$/, '');
      finish([...results, new File([blob], `${base}_${ratioId.replace(':', 'x')}.jpg`, { type: 'image/jpeg' })]);
    }, 'image/jpeg', 0.92);
  };

  const useOriginal = () => finish([...results, file]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent className="sm:max-w-xl" data-testid="crop-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Crop className="h-4 w-4 text-gold" /> Crop image {files?.length > 1 ? `(${idx + 1}/${files.length})` : ''}
          </DialogTitle>
          <DialogDescription>
            {lockRatio
              ? 'Carousel: the same aspect ratio is applied to every image for a consistent look on FB + IG.'
              : 'Pick a ratio, then drag the gold frame to position — or drag its corner to resize.'}
          </DialogDescription>
        </DialogHeader>

        {/* Ratio picker — glyph shows the actual frame shape */}
        <div className="grid grid-cols-4 gap-1.5" data-testid="crop-ratio-picker">
          {RATIOS.map((r) => {
            const active = ratioId === r.id;
            return (
              <button key={r.id} type="button"
                      onClick={() => setRatioId(r.id)}
                      disabled={lockRatio && idx > 0}
                      className={`rounded-md border px-2 py-2 flex flex-col items-center gap-1 transition-all disabled:opacity-40 ${
                        active ? 'bg-gold/15 border-gold text-gold shadow-[0_0_0_1px_rgba(212,175,55,0.4)]' : 'border-border/40 text-muted-foreground hover:border-border hover:text-foreground'
                      }`}
                      data-testid={`crop-ratio-${r.id.replace(':', 'x')}`}>
                <span className={`block border-2 rounded-[2px] ${active ? 'border-gold bg-gold/20' : 'border-current opacity-60'}`}
                      style={{ width: r.glyph.w, height: r.glyph.h }} />
                <span className="text-[11px] font-semibold leading-none">{r.label}</span>
                <span className="text-[9px] leading-tight text-center opacity-70">{r.hint}</span>
              </button>
            );
          })}
        </div>
        <p className="text-[10px] text-muted-foreground -mt-1" data-testid="crop-active-ratio-line">
          Cropping to <span className="text-gold font-medium">{ratioDef.label}</span> — {ratioDef.hint}
        </p>

        <div className="flex items-center justify-center bg-black/60 rounded-md overflow-hidden select-none" style={{ minHeight: 220 }}>
          {imgUrl && (
            <div className="relative inline-block" style={{ lineHeight: 0 }}>
              <img ref={imgRef} src={imgUrl} alt="" onLoad={onImgLoad}
                   style={{ maxHeight: 340, maxWidth: '100%', display: 'block', opacity: 0.35 }} draggable={false} />
              {disp && (
                <div className="absolute cursor-move"
                     style={{ left: offset.x, top: offset.y, width: cw, height: ch,
                              border: '2px solid #D4AF37',
                              boxShadow: '0 0 0 9999px rgba(0,0,0,0.72), 0 0 14px rgba(212,175,55,0.55)' }}
                     onMouseDown={startDrag}
                     data-testid="crop-window">
                  <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    <img src={imgUrl} alt="" draggable={false}
                         style={{ position: 'absolute', left: -offset.x, top: -offset.y, width: disp.w, height: disp.h, maxWidth: 'none' }} />
                  </div>
                  {/* Rule-of-thirds grid */}
                  <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute left-1/3 top-0 bottom-0 w-px bg-white/30" />
                    <div className="absolute left-2/3 top-0 bottom-0 w-px bg-white/30" />
                    <div className="absolute top-1/3 left-0 right-0 h-px bg-white/30" />
                    <div className="absolute top-2/3 left-0 right-0 h-px bg-white/30" />
                  </div>
                  {/* Corner marks */}
                  {[['top-0 left-0', 'border-t-2 border-l-2'], ['top-0 right-0', 'border-t-2 border-r-2'],
                    ['bottom-0 left-0', 'border-b-2 border-l-2'], ['bottom-0 right-0', 'border-b-2 border-r-2']].map(([pos, b]) => (
                    <span key={pos} className={`absolute ${pos} h-4 w-4 ${b} border-gold pointer-events-none`} />
                  ))}
                  {/* Live ratio tag */}
                  <span className="absolute -top-6 left-0 text-[10px] font-semibold text-gold bg-black/70 px-1.5 py-0.5 rounded pointer-events-none">
                    {ratioDef.label}
                  </span>
                  {/* Resize handle */}
                  <span onMouseDown={startResize}
                        className="absolute -bottom-2 -right-2 h-4 w-4 rounded-full bg-gold border-2 border-black cursor-nwse-resize"
                        data-testid="crop-resize-handle" />
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={useOriginal} data-testid="crop-use-original">
            <SkipForward className="h-3.5 w-3.5 mr-1" /> Use original
          </Button>
          <Button type="button" onClick={applyCrop} disabled={!disp} data-testid="crop-apply">
            <Check className="h-3.5 w-3.5 mr-1" /> Apply crop
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
